import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, Form, Button, Row, Col, Alert, Spinner, Table, Collapse, Badge, Modal, ButtonGroup } from "react-bootstrap";
import axios from "axios";
import { buildKsefInvoiceNote, computeKsefRefund } from "../../utils/ksefInvoiceNote";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatApiError = (err, fallback) => {
  const error = err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
  const hint = err?.response?.data?.errorHint;
  return hint ? `${error}\n${hint}` : error;
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Tak" : "Nie";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatAmount = (value) => (value == null || value === "" ? "—" : Number(value).toFixed(2));

const DetailRow = ({ label, value }) => (
  <tr>
    <th style={{ width: "35%" }}>{label}</th>
    <td>{displayValue(value)}</td>
  </tr>
);

const lastDayOfMonth = (year, month) => {
  const last = new Date(Number(year), Number(month), 0);
  const dd = String(last.getDate()).padStart(2, "0");
  const mm = String(Number(month)).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
};

const monthToRange = (year, month) => {
  if (!year || !month) return null;
  const mm = String(Number(month)).padStart(2, "0");
  return { dateFrom: `${year}-${mm}-01`, dateTo: lastDayOfMonth(year, month) };
};

const matchTypeBadge = (matchType) => (matchType === "carNumber" ? "primary" : "info");
const matchTypeLabel = (matchType, t) => (matchType === "carNumber" ? t("ksefMatchByCar") : t("ksefMatchByKsefId"));

const normalizeMode = (mode) => String(mode || "").trim().toLowerCase();

const uniqueMatchDrivers = (matches) => {
  const seen = new Map();
  (matches || []).forEach((m) => {
    if (!seen.has(m.driverId)) seen.set(m.driverId, m);
  });
  return Array.from(seen.values());
};

const PLATE_FIELD_RE = /nr\.?\s*pojazdu|numer\s+rejestracyjny|nr\.?\s*rej|tablic|rejestrac/i;

/** Поля DodatkowyOpis (Klucz/Wartosc) — саме тут Shell/Alicja кладуть nr rej. */
const getDodatkowyOpis = (invoice) => {
  const candidates = invoice?.xml?.extracted?.lineItemCandidates || [];
  const rows = [];
  for (const block of candidates) {
    if (!/DodatkowyOpis/i.test(block.path || "")) continue;
    for (const item of block.items || []) {
      const key = item?.Klucz != null ? String(item.Klucz).trim() : "";
      const value = item?.Wartosc != null ? String(item.Wartosc).trim() : "";
      if (key && value) rows.push({ key, value });
    }
  }
  return rows;
};

const getVehiclePlateInfo = (invoice) => {
  const extras = getDodatkowyOpis(invoice);
  const field = extras.find((e) => PLATE_FIELD_RE.test(e.key));
  const carMatch = (invoice?.matches || []).find((m) => m.matchType === "carNumber");
  const raw = field?.value || null;
  const normalized = carMatch?.matchedValue || null;
  const compact = raw
    ? String(raw).replace(/\s*\([^)]*\)\s*/g, " ").replace(/\s+/g, " ").trim()
    : null;
  return {
    label: field?.key || null,
    raw,
    normalized,
    compact: compact || normalized || null,
    display: raw || normalized || null,
  };
};

const invoiceNoteFromKsef = (invoice, invoiceDefaults) => {
  const refundAmount = computeKsefRefund(
    invoice.vatAmount,
    invoiceDefaults.invoiceFee,
    invoiceDefaults.refundPercent
  );
  const plate = getVehiclePlateInfo(invoice).compact;
  const note = buildKsefInvoiceNote(invoice, {
    plate,
    refund_amount: refundAmount,
    ksef_number: invoice.ksefNumber,
    vat_amount: invoice.vatAmount,
  });
  return { refundAmount, note, plate };
};

const getSellerLabel = (invoice) => invoice?.seller?.name || null;

const getStationLabel = (invoice) => invoice?.thirdSubjects?.[0]?.name || null;

const getLineItems = (invoice) => {
  const candidates = invoice?.xml?.extracted?.lineItemCandidates || [];
  const block = candidates.find((c) => /FaWiersz/i.test(c.path || ""));
  if (!block?.items?.length) return [];
  return block.items.map((item) => ({
    name: item.P_7 || null,
    unit: item.P_8A || null,
    qty: item.P_8B,
    unitPrice: item.P_9B || item.P_9A,
    net: item.P_11,
    gross: item.P_11A,
    vat: item.P_11Vat,
    vatRate: item.P_12,
    gtu: item.GTU,
  }));
};

const getStations = (invoice) => (Array.isArray(invoice?.thirdSubjects) ? invoice.thirdSubjects : []);

/** Pending driver/admin uploads for the KSeF-matched (or manually picked) driver only. */
const scopeDriverUploads = (rec, matchedDrivers, pickedDriverId) => {
  const allowed = new Set(
    (matchedDrivers || []).map((m) => Number(m.driverId)).filter((id) => Number.isFinite(id) && id > 0)
  );
  if (pickedDriverId != null && Number.isFinite(Number(pickedDriverId))) {
    allowed.add(Number(pickedDriverId));
  }
  if (allowed.size === 0) return [];

  const pool = [
    ...(Array.isArray(rec?.driverUploads) ? rec.driverUploads : []),
    ...(Array.isArray(rec?.possibleMatches) ? rec.possibleMatches : []),
  ];
  const seen = new Set();
  return pool.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return (
      allowed.has(Number(row.driver_id))
      && (row.source === "driver" || row.source === "admin")
      && !row.ksef_number
      && ["uploaded", "rejected"].includes(String(row.status || ""))
    );
  });
};

/** Pending uploads for a specific driver (create-block). */
const pendingUploadsForDriver = (rec, driverId) => {
  if (driverId == null) return [];
  const id = Number(driverId);
  const pool = [
    ...(Array.isArray(rec?.driverUploads) ? rec.driverUploads : []),
    ...(Array.isArray(rec?.possibleMatches) ? rec.possibleMatches : []),
  ];
  const seen = new Set();
  return pool.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return (
      Number(row.driver_id) === id
      && (row.source === "driver" || row.source === "admin")
      && !row.ksef_number
      && ["uploaded", "rejected"].includes(String(row.status || ""))
    );
  });
};

/**
 * Локальний (у браузері) лічильник запитів до KSeF: рахуємо кожен запуск "Завантажити з KSeF",
 * щоб адмін бачив, скільки запитів вже було зроблено і скільки часу минуло з останнього.
 */
const KSEF_REQUEST_LOG_KEY = "ksef_request_log_v1";
const REQUEST_LOG_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const loadKsefRequestLog = () => {
  try {
    const raw = localStorage.getItem(KSEF_REQUEST_LOG_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - REQUEST_LOG_MAX_AGE_MS;
    return parsed.filter((ts) => typeof ts === "number" && ts >= cutoff);
  } catch {
    return [];
  }
};

const pushKsefRequestLog = () => {
  const next = [...loadKsefRequestLog(), Date.now()];
  try {
    localStorage.setItem(KSEF_REQUEST_LOG_KEY, JSON.stringify(next));
  } catch {
    // Ігноруємо помилки localStorage
  }
  return next;
};

const formatElapsed = (ms) => {
  if (ms == null || ms < 0) return "—";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h} г ${m} хв тому`;
  if (m > 0) return `${m} хв ${s} с тому`;
  return `${s} с тому`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const KSEF_TABLE_COLS = 12;
const ksefTableMinWidth = { minWidth: 1520 };
const ellipsisCell = {
  maxWidth: 150,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const KsefPage = () => {
  const { t } = useTranslation();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, "0"));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [reconcile, setReconcile] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pickedDriverByKsef, setPickedDriverByKsef] = useState({});
  const [createState, setCreateState] = useState({});
  const [linkState, setLinkState] = useState({});
  const [invoiceDefaults, setInvoiceDefaults] = useState({ invoiceFee: 0, refundPercent: 50 });
  const [requestLog, setRequestLog] = useState(() => loadKsefRequestLog());
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [cacheMeta, setCacheMeta] = useState(null);
  const [modeFilter, setModeFilter] = useState("all"); // all | online | offline
  const [onSiteFilter, setOnSiteFilter] = useState("all"); // all | onSite | notOnSite
  const [processedFilter, setProcessedFilter] = useState("unprocessed"); // all | processed | unprocessed
  const [processState, setProcessState] = useState({}); // { [ksefNumber]: "loading" | error }
  const [statusMessage, setStatusMessage] = useState(null); // коротке повідомлення після дій
  const [driverOptions, setDriverOptions] = useState([]);
  const [showCreateAllModal, setShowCreateAllModal] = useState(false);
  const [createAllRunning, setCreateAllRunning] = useState(false);
  const [createAllProgress, setCreateAllProgress] = useState({ current: 0, total: 0 });
  const [createAllResult, setCreateAllResult] = useState(null);

  const months = useMemo(
    () => [
      { value: "01", label: t("january") },
      { value: "02", label: t("february") },
      { value: "03", label: t("march") },
      { value: "04", label: t("april") },
      { value: "05", label: t("may") },
      { value: "06", label: t("june") },
      { value: "07", label: t("july") },
      { value: "08", label: t("august") },
      { value: "09", label: t("september") },
      { value: "10", label: t("october") },
      { value: "11", label: t("november") },
      { value: "12", label: t("december") },
    ],
    [t]
  );
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => String(currentYear - i));
  }, []);

  const range = useMemo(() => monthToRange(selectedYear, selectedMonth), [selectedYear, selectedMonth]);

  useEffect(() => {
    const intervalId = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const requestsLastHour = useMemo(
    () => requestLog.filter((ts) => nowTick - ts < 60 * 60 * 1000).length,
    [requestLog, nowTick]
  );
  const lastRequestAt = requestLog.length > 0 ? requestLog[requestLog.length - 1] : null;

  useEffect(() => {
    axios.get("/api/ksef/config", { headers: authHeaders() })
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    axios.get("/api/users", { headers: authHeaders() })
      .then((r) => {
        const list = (r.data || [])
          .filter((u) => u.role === "user")
          .map((u) => ({
            value: u.id,
            label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || `#${u.id}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "uk"));
        setDriverOptions(list);
      })
      .catch(() => setDriverOptions([]));
  }, []);

  useEffect(() => {
    axios.get("/api/settings", { headers: authHeaders() })
      .then((r) => {
        const data = r.data || {};
        const fee = data.default_invoiceProcessingPrice != null && data.default_invoiceProcessingPrice !== ""
          ? Number(data.default_invoiceProcessingPrice)
          : 0;
        const percent = data.default_invoiceReturnPercent != null && data.default_invoiceReturnPercent !== ""
          ? Number(data.default_invoiceReturnPercent)
          : 50;
        setInvoiceDefaults({
          invoiceFee: Number.isNaN(fee) ? 0 : fee,
          refundPercent: Number.isNaN(percent) ? 50 : percent,
        });
      })
      .catch(() => {});
  }, []);

  const refreshCacheMeta = useCallback(async () => {
    if (!range) {
      setCacheMeta(null);
      return;
    }
    try {
      const r = await axios.get("/api/ksef/invoices/cache/meta", {
        params: { dateFrom: range.dateFrom, dateTo: range.dateTo },
        headers: authHeaders(),
      });
      setCacheMeta(r.data);
    } catch {
      setCacheMeta(null);
    }
  }, [range]);

  useEffect(() => {
    refreshCacheMeta();
  }, [refreshCacheMeta]);

  const runReconciliation = useCallback(async (invoicesList) => {
    if (!invoicesList || invoicesList.length === 0) {
      setReconcile({});
      return;
    }
    try {
      const items = invoicesList.map((inv) => ({
        ksef_number: inv.ksefNumber,
        gross_amount: inv.grossAmount,
        vat_amount: inv.vatAmount,
        matched_driver_ids: uniqueMatchDrivers(inv.matches).map((m) => m.driverId),
      }));
      const r = await axios.post("/api/driver-invoices/ksef-check", { items }, { headers: authHeaders() });
      setReconcile(r.data?.results || {});
    } catch (err) {
      console.error("Помилка звірки KSeF з базою:", err);
      setReconcile({});
    }
  }, []);

  const applyLoadedResponse = async (data) => {
    setResponse(data);
    await runReconciliation(data?.invoices || []);
    await refreshCacheMeta();
  };

  const loadFromCache = async () => {
    setError(null);
    setResponse(null);
    setReconcile({});
    setCreateState({});
    setLinkState({});
    if (!range) {
      setError(t("ksefErrorMonthRequired"));
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get("/api/ksef/invoices/cache", {
        params: { dateFrom: range.dateFrom, dateTo: range.dateTo },
        headers: authHeaders(),
      });
      await applyLoadedResponse(r.data);
    } catch (err) {
      setError(formatApiError(err, t("ksefErrorCache")));
      setResponse(err.response?.data || null);
    } finally {
      setLoading(false);
    }
  };

  const fetchFromKsef = async () => {
    setError(null);
    setResponse(null);
    setReconcile({});
    setCreateState({});
    setLinkState({});
    if (!range) {
      setError(t("ksefErrorMonthRequired"));
      return;
    }
    setLoading(true);
    setRequestLog(pushKsefRequestLog());
    try {
      const r = await axios.post(
        "/api/ksef/invoices",
        { dateFrom: range.dateFrom, dateTo: range.dateTo },
        { headers: authHeaders() }
      );
      await applyLoadedResponse(r.data);
    } catch (err) {
      setError(formatApiError(err, t("ksefErrorFetch")));
      setResponse(err.response?.data || null);
    } finally {
      setLoading(false);
    }
  };

  const invoices = Array.isArray(response?.invoices) ? response.invoices : [];

  const isEffectivelyProcessed = useCallback((inv, rec = null, createStatus = null) => {
    const reconcileRow = rec || reconcile[inv.ksefNumber] || {};
    const status = createStatus != null ? createStatus : createState[inv.ksefNumber];
    return Boolean(inv.isProcessed)
      || Boolean(inv.createdOnSite)
      || (reconcileRow.exactMatches || []).length > 0
      || status === "done";
  }, [reconcile, createState]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (modeFilter !== "all") {
        const mode = normalizeMode(inv.invoicingMode);
        if (modeFilter === "online" && mode !== "online") return false;
        if (modeFilter === "offline" && mode !== "offline") return false;
      }
      if (onSiteFilter !== "all") {
        const rec = reconcile[inv.ksefNumber] || {};
        const onSite =
          (rec.exactMatches || []).length > 0 ||
          (rec.possibleMatches || []).length > 0 ||
          (rec.driverUploads || []).length > 0 ||
          createState[inv.ksefNumber] === "done" ||
          Boolean(inv.createdOnSite);
        if (onSiteFilter === "onSite" && !onSite) return false;
        if (onSiteFilter === "notOnSite" && onSite) return false;
      }
      if (processedFilter !== "all") {
        const processed = isEffectivelyProcessed(inv);
        if (processedFilter === "processed" && !processed) return false;
        if (processedFilter === "unprocessed" && processed) return false;
      }
      return true;
    });
  }, [invoices, modeFilter, onSiteFilter, processedFilter, reconcile, createState, isEffectivelyProcessed]);

  const markProcessed = useCallback(async (invoice, nextProcessed) => {
    const r = await axios.patch(
      `/api/ksef/invoices/cache/${encodeURIComponent(invoice.ksefNumber)}/processed`,
      { processed: nextProcessed, invoice },
      { headers: authHeaders() }
    );
    const isProcessed = Boolean(r.data?.isProcessed);
    setResponse((prev) => {
      if (!prev?.invoices) return prev;
      return {
        ...prev,
        invoices: prev.invoices.map((inv) =>
          inv.ksefNumber === invoice.ksefNumber
            ? {
                ...inv,
                isProcessed,
                processedAt: r.data?.processedAt || null,
                processedBy: r.data?.processedBy || null,
              }
            : inv
        ),
      };
    });
    return isProcessed;
  }, []);

  const handleDriverPick = (ksefNumber, driverId) => {
    setPickedDriverByKsef((prev) => ({ ...prev, [ksefNumber]: driverId }));
  };

  const handleToggleProcessed = async (invoice, nextProcessed) => {
    setProcessState((prev) => ({ ...prev, [invoice.ksefNumber]: "loading" }));
    try {
      await markProcessed(invoice, nextProcessed);
      setProcessState((prev) => ({ ...prev, [invoice.ksefNumber]: null }));
      setStatusMessage(
        nextProcessed
          ? t("ksefMarkedProcessedMsg", { number: invoice.invoiceNumber || invoice.ksefNumber })
          : t("ksefMarkedUnprocessedMsg", { number: invoice.invoiceNumber || invoice.ksefNumber })
      );
      await refreshCacheMeta();
    } catch (err) {
      setProcessState((prev) => ({
        ...prev,
        [invoice.ksefNumber]: formatApiError(err, t("ksefProcessError")),
      }));
    }
  };

  const markInvoiceOnSite = useCallback((ksefNumber) => {
    setResponse((prev) => {
      if (!prev?.invoices) return prev;
      return {
        ...prev,
        invoices: prev.invoices.map((inv) =>
          inv.ksefNumber === ksefNumber
            ? { ...inv, isProcessed: true, createdOnSite: true }
            : inv
        ),
      };
    });
  }, []);

  const confirmInvoiceOnSite = async (invoice, options = {}) => {
    const {
      driverId: forcedDriverId = null,
      existingInvoiceId = null,
      silent = false,
    } = options;
    const drivers = uniqueMatchDrivers(invoice.matches);
    const driverId = forcedDriverId || pickedDriverByKsef[invoice.ksefNumber] || drivers[0]?.driverId;
    if (!driverId) {
      const msg = t("ksefPickDriverRequired");
      if (!silent) setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: msg }));
      return { ok: false, reason: "noDriver", message: msg };
    }

    const rec = reconcile[invoice.ksefNumber] || {};
    const driverUploads = pendingUploadsForDriver(rec, driverId);
    let uploadId = existingInvoiceId;
    if (uploadId == null && driverUploads.length === 1) {
      uploadId = driverUploads[0].id;
    }
    if (uploadId == null && driverUploads.length > 1) {
      const msg = t("ksefCreateBlockedDriverUpload");
      if (!silent) setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: msg }));
      return { ok: false, reason: "multiUpload", message: msg };
    }

    const { refundAmount, note } = invoiceNoteFromKsef(invoice, invoiceDefaults);
    const fee = Number(invoiceDefaults.invoiceFee) || 0;
    const percent = invoiceDefaults.refundPercent != null ? Number(invoiceDefaults.refundPercent) : 50;

    if (uploadId) {
      setLinkState((prev) => ({ ...prev, [uploadId]: "loading" }));
      if (!silent) setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: "loading" }));
      try {
        await axios.post(
          `/api/driver-invoices/${uploadId}/ksef-confirm`,
          {
            ksef_number: invoice.ksefNumber,
            ksef_data: invoice,
            gross_amount: invoice.grossAmount,
            vat_amount: invoice.vatAmount,
            invoice_fee: fee,
            refund_percent: percent,
            note,
          },
          { headers: authHeaders() }
        );
        setLinkState((prev) => ({ ...prev, [uploadId]: "done" }));
        markInvoiceOnSite(invoice.ksefNumber);
        try {
          await markProcessed(invoice, true);
        } catch (markErr) {
          console.warn("Не вдалося зберегти позначку оброблено після підтвердження:", markErr);
        }
        if (!silent) {
          setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: "done" }));
          setStatusMessage(t("ksefDriverConfirmedMsg", { number: invoice.invoiceNumber || invoice.ksefNumber }));
          await runReconciliation(invoices);
          await refreshCacheMeta();
        }
        return { ok: true, action: "confirm" };
      } catch (err) {
        const msg = formatApiError(err, t("ksefConfirmDriverError"));
        setLinkState((prev) => ({ ...prev, [uploadId]: msg }));
        if (!silent) setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: msg }));
        return { ok: false, reason: "confirmError", message: msg };
      }
    }

    if (!silent) setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: "loading" }));
    try {
      await axios.post(
        "/api/driver-invoices",
        {
          driver_id: driverId,
          source: "ksef",
          status: "approved",
          gross_amount: invoice.grossAmount,
          vat_amount: invoice.vatAmount,
          refund_amount: refundAmount,
          invoice_fee: fee,
          refund_percent: percent,
          approved_at: new Date().toISOString().slice(0, 19).replace("T", " "),
          note,
          ksef_number: invoice.ksefNumber,
          ksef_data: invoice,
        },
        { headers: authHeaders() }
      );
      markInvoiceOnSite(invoice.ksefNumber);
      try {
        await markProcessed(invoice, true);
      } catch (markErr) {
        console.warn("Не вдалося зберегти позначку оброблено після створення:", markErr);
      }
      if (!silent) {
        setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: "done" }));
        setStatusMessage(t("ksefCreatedAndProcessedMsg", { number: invoice.invoiceNumber || invoice.ksefNumber }));
        await runReconciliation(invoices);
        await refreshCacheMeta();
      }
      return { ok: true, action: "create" };
    } catch (err) {
      const code = err?.response?.data?.errorCode;
      if (code === "DRIVER_UPLOAD_EXISTS") {
        const msg = t("ksefCreateBlockedDriverUpload");
        if (!silent) {
          setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: msg }));
          await runReconciliation(invoices);
        }
        return { ok: false, reason: "driverUpload", message: msg };
      }
      const msg = formatApiError(err, t("ksefCreateError"));
      if (!silent) setCreateState((prev) => ({ ...prev, [invoice.ksefNumber]: msg }));
      return { ok: false, reason: "createError", message: msg };
    }
  };

  const handleCreateInvoice = async (invoice) => {
    await confirmInvoiceOnSite(invoice);
  };

  /** Driver-primary: прив'язати KSeF до завантаження водія і одразу затвердити. */
  const handleConfirmDriverUpload = async (invoice, existingInvoiceId) => {
    await confirmInvoiceOnSite(invoice, { existingInvoiceId });
  };

  const handleLinkExisting = async (invoice, existingInvoiceId) => {
    // Якщо це завантаження водія/адміна без ksef — підтверджуємо лише для matched/picked водія
    const rec = reconcile[invoice.ksefNumber] || {};
    const candidate = [...(rec.driverUploads || []), ...(rec.possibleMatches || [])]
      .find((pm) => pm.id === existingInvoiceId);
    if (candidate && !candidate.ksef_number && ["uploaded", "rejected"].includes(candidate.status)) {
      const matchedIds = new Set(
        uniqueMatchDrivers(invoice.matches).map((m) => Number(m.driverId))
      );
      const picked = pickedDriverByKsef[invoice.ksefNumber];
      if (picked != null) matchedIds.add(Number(picked));
      if (matchedIds.size === 0 || matchedIds.has(Number(candidate.driver_id))) {
        return handleConfirmDriverUpload(invoice, existingInvoiceId);
      }
      setLinkState((prev) => ({
        ...prev,
        [existingInvoiceId]: t("ksefCreateBlockedDriverUpload"),
      }));
      return;
    }

    setLinkState((prev) => ({ ...prev, [existingInvoiceId]: "loading" }));
    try {
      await axios.put(
        `/api/driver-invoices/${existingInvoiceId}`,
        { ksef_number: invoice.ksefNumber, ksef_data: invoice },
        { headers: authHeaders() }
      );
      setLinkState((prev) => ({ ...prev, [existingInvoiceId]: "done" }));
      setResponse((prev) => {
        if (!prev?.invoices) return prev;
        return {
          ...prev,
          invoices: prev.invoices.map((inv) =>
            inv.ksefNumber === invoice.ksefNumber
              ? { ...inv, isProcessed: true, createdOnSite: true }
              : inv
          ),
        };
      });
      await runReconciliation(invoices);
      try {
        await markProcessed(invoice, true);
      } catch (markErr) {
        console.warn("Не вдалося зберегти позначку оброблено після прив'язки:", markErr);
      }
      setStatusMessage(t("ksefLinkedAndProcessedMsg", { number: invoice.invoiceNumber || invoice.ksefNumber }));
      await refreshCacheMeta();
    } catch (err) {
      setLinkState((prev) => ({ ...prev, [existingInvoiceId]: formatApiError(err, t("ksefLinkError")) }));
    }
  };

  const processedBacklog = useMemo(() => {
    const ready = [];
    const skipped = [];
    for (const inv of invoices) {
      if (!inv.isProcessed) continue;
      const rec = reconcile[inv.ksefNumber] || {};
      const alreadyOnSite =
        (rec.exactMatches || []).length > 0
        || Boolean(inv.createdOnSite)
        || createState[inv.ksefNumber] === "done";
      if (alreadyOnSite) {
        skipped.push({ inv, reason: "alreadyOnSite" });
        continue;
      }
      const drivers = uniqueMatchDrivers(inv.matches);
      const pickedId = pickedDriverByKsef[inv.ksefNumber];
      const driverId = pickedId || (drivers.length === 1 ? drivers[0].driverId : null);
      if (!driverId) {
        skipped.push({ inv, reason: drivers.length > 1 ? "multiDriver" : "noDriver" });
        continue;
      }
      if (drivers.length > 1 && pickedId == null) {
        skipped.push({ inv, reason: "multiDriver" });
        continue;
      }
      const uploads = pendingUploadsForDriver(rec, driverId);
      if (uploads.length > 1) {
        skipped.push({ inv, reason: "multiUpload" });
        continue;
      }
      const { refundAmount, note } = invoiceNoteFromKsef(inv, invoiceDefaults);
      const driverLabel =
        driverOptions.find((d) => Number(d.value) === Number(driverId))?.label
        || drivers.find((d) => Number(d.driverId) === Number(driverId))?.driverName
        || `#${driverId}`;
      ready.push({
        inv,
        driverId,
        driverLabel,
        existingInvoiceId: uploads[0]?.id || null,
        action: uploads.length === 1 ? "confirm" : "create",
        refundAmount,
        note,
      });
    }
    return { ready, skipped };
  }, [invoices, reconcile, createState, pickedDriverByKsef, invoiceDefaults, driverOptions]);

  const skipReasonLabel = (reason) => {
    if (reason === "alreadyOnSite") return t("ksefCreateAllSkipOnSite");
    if (reason === "noDriver") return t("ksefCreateAllSkipNoDriver");
    if (reason === "multiDriver") return t("ksefCreateAllSkipMultiDriver");
    if (reason === "multiUpload") return t("ksefCreateAllSkipMultiUpload");
    return reason;
  };

  const executeCreateAllProcessed = async () => {
    const items = processedBacklog.ready;
    if (!items.length) return;
    setCreateAllRunning(true);
    setCreateAllResult(null);
    setCreateAllProgress({ current: 0, total: items.length });
    const created = [];
    const confirmed = [];
    const errors = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      setCreateAllProgress({ current: i + 1, total: items.length });
      const result = await confirmInvoiceOnSite(item.inv, {
        silent: true,
        driverId: item.driverId,
        existingInvoiceId: item.existingInvoiceId,
      });
      if (result.ok && result.action === "confirm") confirmed.push(item);
      else if (result.ok) created.push(item);
      else errors.push({ item, message: result.message || result.reason });
    }
    await runReconciliation(invoices);
    await refreshCacheMeta();
    setCreateAllRunning(false);
    setCreateAllResult({
      created: created.length,
      confirmed: confirmed.length,
      errors,
    });
    setStatusMessage(
      t("ksefCreateAllDoneMsg", {
        created: created.length,
        confirmed: confirmed.length,
        errors: errors.length,
      })
    );
  };

  const sourceBadge = response?.source === "cache"
    ? <Badge bg="secondary">{t("ksefSourceCache")}</Badge>
    : response?.source === "ksef"
      ? <Badge bg="success">{t("ksefSourceLive")}</Badge>
      : null;

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <h2 className="mb-0">{t("ksefTitle")}</h2>
        {config && (
          <>
            <Badge bg={config.environment === "demo" ? "info" : "secondary"}>{config.label}</Badge>
            <Badge bg={config.tokenConfigured ? "success" : "danger"}>
              {config.tokenConfigured ? t("ksefTokenConfigured") : t("ksefTokenMissing")}
            </Badge>
            {config.nip && <span className="small text-muted">NIP: {config.nip}</span>}
            {config.docsUrl && (
              <a href={config.docsUrl} target="_blank" rel="noopener noreferrer" className="small">
                {t("ksefInstructionDoc")} →
              </a>
            )}
          </>
        )}
      </div>
      <p className="text-muted mb-4">{t("ksefDescription")}</p>

      {config && !config.tokenConfigured && (
        <Alert variant="warning">
          {t("ksefTokenMissingHint")}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>{t("ksefParams")}</Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label>{t("selectMonth")}</Form.Label>
              <Form.Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>{t("year")}</Form.Label>
              <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              {range && (
                <div className="small text-muted">
                  {t("ksefDateFrom")}: <strong>{range.dateFrom}</strong> — {t("ksefDateTo")}: <strong>{range.dateTo}</strong>
                </div>
              )}
              {cacheMeta && (
                <div className="small text-muted mt-1">
                  {t("ksefCacheInfo", { count: cacheMeta.cachedCount || 0 })}
                  {cacheMeta.processedCount != null && (
                    <> · {t("ksefCacheProcessed", { count: cacheMeta.processedCount || 0 })}</>
                  )}
                  {cacheMeta.lastCachedAt && (
                    <> · {t("ksefCacheUpdated")}: <strong>{formatDateTime(cacheMeta.lastCachedAt)}</strong></>
                  )}
                </div>
              )}
            </Col>
            <Col md={4}>
              <div className="d-flex flex-column gap-2">
                <Button
                  variant="outline-primary"
                  onClick={loadFromCache}
                  disabled={loading || !range}
                >
                  {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                  {t("ksefLoadFromCache")}
                </Button>
                <Button
                  variant="primary"
                  onClick={fetchFromKsef}
                  disabled={loading || !range || (config && !config.tokenConfigured)}
                >
                  {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                  {t("ksefFetchButton")}
                </Button>
              </div>
            </Col>
          </Row>

          <div className="d-flex flex-wrap align-items-center gap-3 mt-3 small text-muted">
            <span>
              {t("ksefRequestsLastHour")}: <Badge bg={requestsLastHour > 0 ? "secondary" : "light"} text={requestsLastHour > 0 ? undefined : "dark"}>{requestsLastHour}</Badge>
            </span>
            <span>
              {t("ksefLastRequest")}: <strong>{lastRequestAt ? formatElapsed(nowTick - lastRequestAt) : t("ksefNeverRequested")}</strong>
            </span>
          </div>

          {error && (
            <Alert variant="danger" className="mt-3 mb-0" style={{ whiteSpace: "pre-line" }} onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          {statusMessage && (
            <Alert variant="success" className="mt-3 mb-0" onClose={() => setStatusMessage(null)} dismissible>
              {statusMessage}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {response && !response.error && (
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="d-flex align-items-center gap-2 flex-wrap">
              {t("ksefResults")} — {filteredInvoices.length > 0
                ? t("ksefInvoicesCount", { count: filteredInvoices.length })
                : t("ksefNoInvoices")}
              {filteredInvoices.length !== invoices.length && (
                <span className="small text-muted">({t("ksefFilteredOf", { total: invoices.length })})</span>
              )}
              {sourceBadge}
            </span>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {processedBacklog.ready.length > 0 || processedBacklog.skipped.some((s) => s.reason !== "alreadyOnSite") ? (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => {
                    setCreateAllResult(null);
                    setShowCreateAllModal(true);
                  }}
                  disabled={createAllRunning}
                >
                  {t("ksefCreateAllProcessed")} ({processedBacklog.ready.length})
                </Button>
              ) : null}
              <Button variant="outline-secondary" size="sm" onClick={() => setShowRaw(!showRaw)}>
                {showRaw ? t("ksefHideRaw") : t("ksefShowRaw")}
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Label className="small mb-1">{t("ksefFilterMode")}</Form.Label>
                <ButtonGroup size="sm" className="w-100 flex-wrap">
                  <Button variant={modeFilter === "all" ? "primary" : "outline-primary"} onClick={() => setModeFilter("all")}>
                    {t("ksefFilterAll")}
                  </Button>
                  <Button variant={modeFilter === "online" ? "primary" : "outline-primary"} onClick={() => setModeFilter("online")}>
                    Online
                  </Button>
                  <Button variant={modeFilter === "offline" ? "primary" : "outline-primary"} onClick={() => setModeFilter("offline")}>
                    Offline
                  </Button>
                </ButtonGroup>
              </Col>
              <Col md={4}>
                <Form.Label className="small mb-1">{t("ksefFilterOnSite")}</Form.Label>
                <ButtonGroup size="sm" className="w-100 flex-wrap">
                  <Button variant={onSiteFilter === "all" ? "secondary" : "outline-secondary"} onClick={() => setOnSiteFilter("all")}>
                    {t("ksefFilterAll")}
                  </Button>
                  <Button variant={onSiteFilter === "notOnSite" ? "secondary" : "outline-secondary"} onClick={() => setOnSiteFilter("notOnSite")}>
                    {t("ksefFilterNotOnSite")}
                  </Button>
                  <Button variant={onSiteFilter === "onSite" ? "secondary" : "outline-secondary"} onClick={() => setOnSiteFilter("onSite")}>
                    {t("ksefFilterAlreadyOnSite")}
                  </Button>
                </ButtonGroup>
              </Col>
              <Col md={4}>
                <Form.Label className="small mb-1">{t("ksefFilterProcessed")}</Form.Label>
                <ButtonGroup size="sm" className="w-100 flex-wrap">
                  <Button variant={processedFilter === "unprocessed" ? "dark" : "outline-dark"} onClick={() => setProcessedFilter("unprocessed")}>
                    {t("ksefFilterUnprocessed")}
                  </Button>
                  <Button variant={processedFilter === "processed" ? "dark" : "outline-dark"} onClick={() => setProcessedFilter("processed")}>
                    {t("ksefFilterProcessedOnly")}
                  </Button>
                  <Button variant={processedFilter === "all" ? "dark" : "outline-dark"} onClick={() => setProcessedFilter("all")}>
                    {t("ksefFilterAll")}
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>

            {Array.isArray(response.subjectSummaries) && response.subjectSummaries.some((s) => s.error) && (
              <Alert variant="warning" className="small mb-3">
                {response.subjectSummaries.filter((s) => s.error).map((s) => (
                  <div key={s.subjectType}>{s.subjectType}: {s.error}</div>
                ))}
              </Alert>
            )}
            {filteredInvoices.length > 0 ? (
              <div className="table-responsive">
                <Table striped bordered size="sm" className="mb-0 align-middle" style={ksefTableMinWidth}>
                  <thead>
                    <tr>
                      <th className="text-nowrap">{t("ksefTableDate")}</th>
                      <th className="text-nowrap">{t("ksefTableNumber")}</th>
                      <th>KSeF ID</th>
                      <th>{t("ksefTableSeller")}</th>
                      <th className="text-nowrap">{t("ksefVehiclePlate")}</th>
                      <th className="text-nowrap">{t("ksefTableMode")}</th>
                      <th className="text-end text-nowrap">Netto</th>
                      <th className="text-end text-nowrap">VAT</th>
                      <th className="text-end text-nowrap">Brutto</th>
                      <th className="text-nowrap">{t("ksefOnSite")}</th>
                      <th>{t("ksefMatches")}</th>
                      <th style={{ minWidth: 240, width: 240 }}>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((inv) => {
                      const rec = reconcile[inv.ksefNumber] || { exactMatches: [], possibleMatches: [], driverUploads: [] };
                      const drivers = uniqueMatchDrivers(inv.matches);
                      const createStatus = createState[inv.ksefNumber];
                      const pickedId = pickedDriverByKsef[inv.ksefNumber] ?? drivers[0]?.driverId ?? null;
                      const alreadyLinked = rec.exactMatches?.length > 0 || Boolean(inv.createdOnSite);
                      const processStatus = processState[inv.ksefNumber];
                      const driverUploads = scopeDriverUploads(rec, drivers, pickedId);
                      const amountMatches = rec.possibleMatches || [];
                      const otherMatches = amountMatches.filter(
                        (pm) => !driverUploads.some((du) => du.id === pm.id)
                      );
                      const hasDriverUpload = !alreadyLinked && driverUploads.length > 0;
                      const orphanUploads = alreadyLinked ? driverUploads : [];
                      const effectivelyProcessed = isEffectivelyProcessed(inv, rec, createStatus);
                      const sellerName = getSellerLabel(inv);
                      const stationName = getStationLabel(inv);
                      const plate = getVehiclePlateInfo(inv);
                      return (
                        <React.Fragment key={inv.ksefNumber}>
                          <tr className={effectivelyProcessed ? "table-secondary" : undefined}>
                            <td style={{ cursor: "pointer" }} onClick={() => setSelectedInvoice(inv)}>
                              {(inv.issueDate || "").slice(0, 10) || "—"}
                              <div className="d-flex flex-wrap gap-1 mt-1">
                                {effectivelyProcessed && (
                                  <Badge bg="dark">{t("ksefProcessedBadge")}</Badge>
                                )}
                                {(alreadyLinked || createStatus === "done") && (
                                  <Badge bg="success">{t("ksefCreatedOnSiteBadge")}</Badge>
                                )}
                              </div>
                            </td>
                            <td style={{ cursor: "pointer" }} onClick={() => setSelectedInvoice(inv)}>
                              {inv.invoiceNumber || "—"}
                            </td>
                            <td
                              className="small"
                              style={{ cursor: "pointer", ...ellipsisCell }}
                              title={inv.ksefNumber}
                              onClick={() => setSelectedInvoice(inv)}
                            >
                              {inv.ksefNumber}
                            </td>
                            <td style={{ cursor: "pointer", maxWidth: 180 }} onClick={() => setSelectedInvoice(inv)}>
                              <div className="text-break">{sellerName || "—"}</div>
                              {stationName && (
                                <div className="small text-muted text-break">{stationName}</div>
                              )}
                            </td>
                            <td className="text-nowrap" style={{ cursor: "pointer" }} onClick={() => setSelectedInvoice(inv)}>
                              {plate.compact ? (
                                <code className="small">{plate.compact}</code>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {inv.invoicingMode ? (
                                <Badge bg={normalizeMode(inv.invoicingMode) === "offline" ? "warning" : "info"} text={normalizeMode(inv.invoicingMode) === "offline" ? "dark" : undefined}>
                                  {inv.invoicingMode}
                                </Badge>
                              ) : "—"}
                            </td>
                            <td className="text-end text-nowrap">{formatAmount(inv.netAmount)}</td>
                            <td className="text-end text-nowrap">{formatAmount(inv.vatAmount)}</td>
                            <td className="text-end text-nowrap fw-semibold">{formatAmount(inv.grossAmount)}</td>
                            <td>
                              {alreadyLinked ? (
                                <Badge bg="success">{t("ksefAlreadyExists")}</Badge>
                              ) : hasDriverUpload ? (
                                <Badge bg="info">{t("ksefDriverUploadMatch", { count: driverUploads.length })}</Badge>
                              ) : amountMatches.length > 0 ? (
                                <Badge bg="warning" text="dark">{t("ksefPossibleMatch", { count: amountMatches.length })}</Badge>
                              ) : (
                                <Badge bg="light" text="dark">{t("ksefNotFound")}</Badge>
                              )}
                            </td>
                            <td style={{ maxWidth: 180 }}>
                              {drivers.length > 0 ? (
                                <div className="d-flex flex-column gap-1">
                                  {drivers.map((m) => (
                                    <div key={`${m.driverId}-${m.matchType}`}>
                                      <Badge bg={matchTypeBadge(m.matchType)} className="text-start text-wrap">
                                        {m.driverName}
                                      </Badge>
                                      <div className="small text-muted">
                                        {m.matchedValue ? `${m.matchedValue} · ` : ""}
                                        {matchTypeLabel(m.matchType, t)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted small">{t("ksefNoAutoMatch")}</span>
                              )}
                            </td>
                            <td style={{ minWidth: 240, width: 240, verticalAlign: "top" }}>
                              <div className="d-flex flex-column gap-1">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  className="w-100 text-nowrap"
                                  onClick={() => setSelectedInvoice(inv)}
                                >
                                  {t("ksefViewInvoice")}
                                </Button>
                                <Button
                                  size="sm"
                                  variant={effectivelyProcessed ? "outline-dark" : "dark"}
                                  className="w-100 text-nowrap"
                                  disabled={
                                    processStatus === "loading"
                                    || (effectivelyProcessed && alreadyLinked)
                                  }
                                  title={
                                    effectivelyProcessed && alreadyLinked
                                      ? t("ksefCannotUnprocessCreated")
                                      : undefined
                                  }
                                  onClick={() => handleToggleProcessed(inv, !effectivelyProcessed)}
                                >
                                  {processStatus === "loading"
                                    ? <Spinner animation="border" size="sm" />
                                    : effectivelyProcessed
                                      ? t("ksefMarkUnprocessed")
                                      : t("ksefMarkProcessed")}
                                </Button>
                              {typeof processStatus === "string" && processStatus !== "loading" && (
                                <span className="small text-danger">{processStatus}</span>
                              )}
                              {alreadyLinked ? (
                                <span className="small text-success">{t("ksefAlreadyLinked")}</span>
                              ) : createStatus === "done" ? (
                                <span className="small text-success">{t("ksefCreated")}</span>
                              ) : (
                                <>
                                  {hasDriverUpload && (
                                    <Alert
                                      variant="info"
                                      className="py-1 px-2 mb-0 small"
                                    >
                                      {t("ksefPreferConfirmDriverUpload")}
                                    </Alert>
                                  )}
                                  <Form.Select
                                    size="sm"
                                    value={pickedId || ""}
                                    onChange={(e) =>
                                      handleDriverPick(
                                        inv.ksefNumber,
                                        e.target.value ? Number(e.target.value) : null
                                      )
                                    }
                                    disabled={hasDriverUpload && drivers.length > 0}
                                  >
                                    <option value="">{t("ksefPickDriver")}</option>
                                    {driverOptions.map((d) => (
                                      <option key={d.value} value={d.value}>{d.label}</option>
                                    ))}
                                  </Form.Select>
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    className="w-100 text-nowrap"
                                    disabled={
                                      createStatus === "loading"
                                      || !pickedId
                                      || (hasDriverUpload && driverUploads.length > 1)
                                    }
                                    onClick={() => handleCreateInvoice(inv)}
                                    title={
                                      hasDriverUpload && driverUploads.length > 1
                                        ? t("ksefCreateBlockedDriverUpload")
                                        : invoiceNoteFromKsef(inv, invoiceDefaults).note
                                    }
                                  >
                                    {createStatus === "loading" ? <Spinner animation="border" size="sm" /> : t("ksefConfirmInvoice")}
                                  </Button>
                                  {typeof createStatus === "string" && createStatus !== "loading" && createStatus !== "done" && (
                                    <span className="small text-danger text-break">{createStatus}</span>
                                  )}
                                </>
                              )}
                              </div>
                            </td>
                          </tr>
                          {hasDriverUpload && (
                            <tr>
                              <td colSpan={KSEF_TABLE_COLS} className="bg-info-subtle">
                                <div className="small mb-1 fw-semibold text-primary">{t("ksefDriverUploadsTitle")}</div>
                                <div className="d-flex flex-column gap-1">
                                  {driverUploads.map((du) => (
                                    <div key={du.id} className="d-flex align-items-center gap-2 small flex-wrap">
                                      <span>
                                        #{du.id} · {du.driver_name}
                                        {" · "}Brutto {formatAmount(du.gross_amount)}
                                        {" · "}VAT {formatAmount(du.vat_amount)}
                                        {" · "}{du.status}
                                        {du.source ? ` · ${du.source}` : ""}
                                      </span>
                                      {linkState[du.id] === "done" ? (
                                        <span className="text-success">{t("ksefDriverConfirmed")}</span>
                                      ) : (
                                        <Button
                                          size="sm"
                                          variant="primary"
                                          disabled={linkState[du.id] === "loading"}
                                          onClick={() => handleConfirmDriverUpload(inv, du.id)}
                                        >
                                          {linkState[du.id] === "loading"
                                            ? <Spinner animation="border" size="sm" />
                                            : t("ksefConfirmDriverUpload")}
                                        </Button>
                                      )}
                                      {typeof linkState[du.id] === "string" && !["loading", "done"].includes(linkState[du.id]) && (
                                        <span className="text-danger">{linkState[du.id]}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                          {orphanUploads.length > 0 && (
                            <tr>
                              <td colSpan={KSEF_TABLE_COLS} className="bg-warning-subtle">
                                <div className="small mb-1 fw-semibold text-warning-emphasis">
                                  {t("ksefOrphanDriverUploadsTitle")}
                                </div>
                                <div className="d-flex flex-column gap-1">
                                  {orphanUploads.map((du) => (
                                    <div key={du.id} className="small">
                                      #{du.id} · {du.driver_name}
                                      {" · "}Brutto {formatAmount(du.gross_amount)}
                                      {" · "}VAT {formatAmount(du.vat_amount)}
                                      {" · "}{du.status}
                                      {du.source ? ` · ${du.source}` : ""}
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                          {otherMatches.length > 0 && !alreadyLinked && (
                            <tr>
                              <td colSpan={KSEF_TABLE_COLS} className="bg-light">
                                <div className="small mb-1 fw-semibold">{t("ksefPossibleMatchesTitle")}</div>
                                <div className="d-flex flex-column gap-1">
                                  {otherMatches.map((pm) => {
                                    const isPendingUpload =
                                      !pm.ksef_number
                                      && (pm.source === "driver" || pm.source === "admin")
                                      && ["uploaded", "rejected"].includes(String(pm.status || ""));
                                    const canActOnPending =
                                      !isPendingUpload
                                      || driverUploads.some((du) => du.id === pm.id)
                                      || (pickedId != null && Number(pm.driver_id) === Number(pickedId));
                                    return (
                                    <div key={pm.id} className="d-flex align-items-center gap-2 small flex-wrap">
                                      <span>
                                        #{pm.id} · {pm.driver_name}
                                        {" · "}Brutto {formatAmount(pm.gross_amount)}
                                        {" · "}VAT {formatAmount(pm.vat_amount)}
                                        {" · "}{pm.status}
                                        {pm.source ? ` · ${pm.source}` : ""}
                                        {pm.match_by ? ` · ${pm.match_by}` : ""}
                                        {pm.ksef_number ? ` · KSeF: ${pm.ksef_number}` : ""}
                                      </span>
                                      {!pm.ksef_number && canActOnPending && (
                                        linkState[pm.id] === "done" ? (
                                          <span className="text-success">{t("ksefLinked")}</span>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            disabled={linkState[pm.id] === "loading"}
                                            onClick={() => handleLinkExisting(inv, pm.id)}
                                          >
                                            {linkState[pm.id] === "loading" ? <Spinner animation="border" size="sm" /> : t("ksefLinkExisting")}
                                          </Button>
                                        )
                                      )}
                                      {isPendingUpload && !canActOnPending && (
                                        <span className="text-muted">{t("ksefOtherDriverUploadHint")}</span>
                                      )}
                                      {typeof linkState[pm.id] === "string" && !["loading", "done"].includes(linkState[pm.id]) && (
                                        <span className="text-danger">{linkState[pm.id]}</span>
                                      )}
                                    </div>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p className="text-muted mb-0">{t("ksefNoInvoices")}</p>
            )}
            <Collapse in={showRaw}>
              <div className="mt-3">
                <pre className="bg-dark text-light p-3 rounded small mb-0" style={{ maxHeight: "300px", overflow: "auto" }}>
                  {JSON.stringify(response, null, 2)}
                </pre>
              </div>
            </Collapse>
          </Card.Body>
        </Card>
      )}

      <Modal show={Boolean(selectedInvoice)} onHide={() => setSelectedInvoice(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{selectedInvoice?.invoiceNumber || selectedInvoice?.ksefNumber || "KSeF"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (() => {
            const extraFields = getDodatkowyOpis(selectedInvoice);
            const plate = getVehiclePlateInfo(selectedInvoice);
            const stations = getStations(selectedInvoice);
            const lineItems = getLineItems(selectedInvoice);
            const drivers = uniqueMatchDrivers(selectedInvoice.matches);
            const extraWithoutPlate = extraFields.filter((e) => !PLATE_FIELD_RE.test(e.key));
            return (
            <>
              <div className="mb-3 d-flex flex-wrap gap-2">
                {(selectedInvoice.querySubjectTypes || []).map((s) => <Badge key={s} bg="secondary">{s}</Badge>)}
                {selectedInvoice.invoiceType && <Badge bg="info">{selectedInvoice.invoiceType}</Badge>}
                {selectedInvoice.invoicingMode && <Badge bg="dark">{selectedInvoice.invoicingMode}</Badge>}
                {selectedInvoice.currency && <Badge bg="light" text="dark">{selectedInvoice.currency}</Badge>}
              </div>

              <h6 className="mb-2">{t("ksefTableSubject")}</h6>
              <Table bordered size="sm" className="mb-3">
                <tbody>
                  <DetailRow label="KSeF ID" value={selectedInvoice.ksefNumber} />
                  <DetailRow label={t("ksefTableNumber")} value={selectedInvoice.invoiceNumber} />
                  <DetailRow label={t("ksefTableDate")} value={selectedInvoice.issueDate} />
                  <DetailRow label={t("ksefTableMode")} value={selectedInvoice.invoicingMode} />
                  <DetailRow label="Netto" value={selectedInvoice.netAmount} />
                  <DetailRow label="VAT" value={selectedInvoice.vatAmount} />
                  <DetailRow label="Brutto" value={selectedInvoice.grossAmount} />
                  <DetailRow
                    label={t("ksefVehiclePlate")}
                    value={plate.display}
                  />
                  <tr>
                    <th style={{ width: "35%" }}>{t("ksefLinkedDriver")}</th>
                    <td>
                      {drivers.length > 0 ? (
                        <div className="d-flex flex-column gap-1">
                          {drivers.map((m) => (
                            <div key={`${m.driverId}-${m.matchType}`}>
                              <strong>{m.driverName}</strong>
                              {m.matchedValue ? (
                                <span className="text-muted"> · {m.matchedValue}</span>
                              ) : null}
                              <div className="small text-muted">
                                {matchTypeLabel(m.matchType, t)}
                                {m.matchedFrom ? ` (${m.matchedFrom})` : ""}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">{t("ksefNoLinkedDriver")}</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <h6 className="mb-2">Sprzedawca</h6>
                  <Table bordered size="sm" className="mb-0">
                    <tbody>
                      <DetailRow label="NIP" value={selectedInvoice.seller?.nip} />
                      <DetailRow label={t("name")} value={selectedInvoice.seller?.name} />
                    </tbody>
                  </Table>
                </Col>
                <Col md={6}>
                  <h6 className="mb-2">Nabywca</h6>
                  <Table bordered size="sm" className="mb-0">
                    <tbody>
                      <DetailRow label={t("name")} value={selectedInvoice.buyer?.name} />
                      <DetailRow label="Identyfikator" value={selectedInvoice.buyer?.identifier?.value} />
                    </tbody>
                  </Table>
                </Col>
              </Row>

              {stations.length > 0 && (
                <>
                  <h6 className="mb-2">{t("ksefStation")}</h6>
                  <Table bordered size="sm" className="mb-3">
                    <tbody>
                      {stations.map((st, idx) => (
                        <React.Fragment key={st.identifier?.value || idx}>
                          <DetailRow label={t("name")} value={st.name} />
                          <DetailRow label="ID" value={st.identifier?.value} />
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {lineItems.length > 0 && (
                <>
                  <h6 className="mb-2">{t("ksefLineItems")}</h6>
                  <Table bordered size="sm" className="mb-3">
                    <thead>
                      <tr>
                        <th>{t("ksefLineItemName")}</th>
                        <th className="text-end">{t("ksefLineItemQty")}</th>
                        <th>{t("ksefLineItemUnit")}</th>
                        <th className="text-end">{t("ksefLineItemPrice")}</th>
                        <th className="text-end">{t("ksefLineItemVatRate")}</th>
                        <th className="text-end">{t("ksefLineItemGross")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>{displayValue(item.name)}</td>
                          <td className="text-end">{displayValue(item.qty)}</td>
                          <td>{displayValue(item.unit)}</td>
                          <td className="text-end">{formatAmount(item.unitPrice)}</td>
                          <td className="text-end">{displayValue(item.vatRate)}</td>
                          <td className="text-end fw-semibold">{formatAmount(item.gross ?? item.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {extraWithoutPlate.length > 0 ? (
                <>
                  <h6 className="mb-2">{t("ksefDescriptionsFromXml")}</h6>
                  <Table bordered size="sm" className="mb-3">
                    <thead>
                      <tr>
                        <th>{t("ksefTableSubject")}</th>
                        <th>{t("ksefDescriptionValue")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extraWithoutPlate.map((item, index) => (
                        <tr key={`${item.key}-${index}`}>
                          <td className="small">{displayValue(item.key)}</td>
                          <td style={{ whiteSpace: "pre-wrap" }}>{displayValue(item.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : (
                !plate.display && (
                  <p className="text-muted small">{t("ksefNoDescriptionsFound")}</p>
                )
              )}

              <h6 className="mb-2">Raw JSON</h6>
              <pre className="bg-dark text-light p-3 rounded small mb-0" style={{ maxHeight: "260px", overflow: "auto" }}>
                {JSON.stringify(selectedInvoice, null, 2)}
              </pre>
            </>
            );
          })()}
        </Modal.Body>
      </Modal>

      <Modal
        show={showCreateAllModal}
        onHide={() => !createAllRunning && setShowCreateAllModal(false)}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton={!createAllRunning}>
          <Modal.Title>{t("ksefCreateAllProcessedTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            {t("ksefCreateAllProcessedHint")}
          </p>
          <Alert variant="info" className="py-2">
            {t("ksefCreateAllSummary", {
              ready: processedBacklog.ready.length,
              skipped: processedBacklog.skipped.filter((s) => s.reason !== "alreadyOnSite").length,
            })}
          </Alert>
          {processedBacklog.ready.length > 0 && (
            <div className="table-responsive mb-3">
              <Table striped bordered size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>{t("ksefTableDate")}</th>
                    <th>{t("ksefTableNumber")}</th>
                    <th>{t("driver")}</th>
                    <th className="text-end">VAT</th>
                    <th className="text-end">{t("refunds")}</th>
                    <th>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {processedBacklog.ready.map((item) => (
                    <tr key={item.inv.ksefNumber}>
                      <td>{(item.inv.issueDate || "").slice(0, 10) || "—"}</td>
                      <td>
                        {item.inv.invoiceNumber || item.inv.ksefNumber}
                        {item.note && <div className="small text-muted">{item.note}</div>}
                      </td>
                      <td>{item.driverLabel}</td>
                      <td className="text-end">{formatAmount(item.inv.vatAmount)}</td>
                      <td className="text-end">{formatAmount(item.refundAmount)}</td>
                      <td>
                        <Badge bg={item.action === "confirm" ? "info" : "primary"}>
                          {item.action === "confirm" ? t("ksefConfirmDriverUpload") : t("ksefConfirmInvoice")}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {processedBacklog.skipped.filter((s) => s.reason !== "alreadyOnSite").length > 0 && (
            <>
              <h6 className="mb-2">{t("ksefCreateAllSkippedTitle")}</h6>
              <ul className="small mb-0">
                {processedBacklog.skipped
                  .filter((s) => s.reason !== "alreadyOnSite")
                  .map((s) => (
                    <li key={s.inv.ksefNumber}>
                      {(s.inv.invoiceNumber || s.inv.ksefNumber)} — {skipReasonLabel(s.reason)}
                    </li>
                  ))}
              </ul>
            </>
          )}
          {createAllRunning && (
            <Alert variant="secondary" className="mt-3 mb-0">
              {t("ksefCreateAllProgress", {
                current: createAllProgress.current,
                total: createAllProgress.total,
              })}
            </Alert>
          )}
          {createAllResult && (
            <Alert variant={createAllResult.errors.length ? "warning" : "success"} className="mt-3 mb-0">
              {t("ksefCreateAllDoneMsg", {
                created: createAllResult.created,
                confirmed: createAllResult.confirmed,
                errors: createAllResult.errors.length,
              })}
              {createAllResult.errors.length > 0 && (
                <ul className="mb-0 mt-2 small">
                  {createAllResult.errors.map((err) => (
                    <li key={err.item.inv.ksefNumber}>
                      {err.item.inv.invoiceNumber || err.item.inv.ksefNumber}: {err.message}
                    </li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateAllModal(false)}
            disabled={createAllRunning}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="success"
            onClick={executeCreateAllProcessed}
            disabled={createAllRunning || processedBacklog.ready.length === 0}
          >
            {createAllRunning ? <Spinner animation="border" size="sm" /> : t("ksefCreateAllProcessedRun")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default KsefPage;
